import Phaser from "phaser";
import type { PlatformAdapter } from "../../adapters/PlatformAdapter";
import { GridPath } from "../entities/GridPath";
import type { Cell } from "../entities/GridPath";
import { Tower } from "../entities/Tower";
import type { Projectile } from "../entities/Projectile";
import { Throne } from "../entities/Throne";
import { WaveManager, DIFFICULTY_CONFIGS } from "../systems/WaveManager";
import type { Difficulty } from "../systems/WaveManager";
import { JuiceSystem } from "../systems/JuiceSystem";
import { SoundSystem } from "../systems/SoundSystem";
import { Background } from "../systems/Background";
import { AnimeFX } from "../systems/AnimeFX";
import { TOWER_LIST, TOWERS } from "../data/towers";
import type { Element, TowerDef, TowerId } from "../types";
import { ELEMENT_LIST, ELEMENT_META } from "../types";

interface GameState {
  lives: number;
  gold: number;
  /** Number of element points researched per element. */
  elements: Record<Element, number>;
  /** Total element points spent + unspent. */
  elementPoints: number;
  /** Total gold ever earned (for end-of-run stats). */
  totalGoldEarned: number;
  /** Game time when GameScene started. */
  startedAt: number;
}

const STARTING_LIVES = 25;
const STARTING_GOLD = 120;

/**
 * Phase 1 GameScene. Grid path + tower placement + element-pick + wave manager.
 * Emits events that UIScene listens for to render HUD.
 */
export class GameScene extends Phaser.Scene {
  private adapter!: PlatformAdapter;
  private grid!: GridPath;
  private waveMgr!: WaveManager;
  private juice!: JuiceSystem;
  private background!: Background;
  anime!: AnimeFX;
  sound2!: SoundSystem;
  /** Boss-tier enemy currently on the field (for the boss HP bar / intro). */
  private activeBoss: import("../entities/Enemy").Enemy | null = null;
  private bossIntroShownFor = new Set<import("../entities/Enemy").Enemy>();
  private throne!: Throne;
  private state!: GameState;

  private towers: Tower[] = [];
  private projectiles: Projectile[] = [];

  /** Highlight overlays for empty cells while placing. */
  private placementMarker?: Phaser.GameObjects.Rectangle;
  private selectedTowerId: TowerId | null = null;

  /** Cell currently selected (existing tower or empty). */
  private selectedCell: Cell | null = null;

  /** Consecutive waves cleared without any leak. Resets to 0 on leak. */
  private perfStreak = 0;
  /** Lives at the moment the current wave started. Used to detect perfect waves. */
  private livesAtWaveStart = 0;
  /** Streak bonus gold awarded this wave (0 if no bonus), passed to the prompt. */
  private streakBonusForPrompt = 0;

  constructor() {
    super({ key: "GameScene" });
  }

  create(): void {
    this.adapter = this.registry.get("adapter") as PlatformAdapter;
    this.cameras.main.setBackgroundColor(0x05060f);

    const { width, height } = this.scale;

    // Living animated cosmic backdrop behind everything (negative depth).
    this.background = new Background(this);

    // Lay out grid centered horizontally, anchored to the top.
    this.grid = new GridPath((width - 10 * 48) / 2, 8);
    this.grid.draw(this);
    this.juice = new JuiceSystem(this);
    this.anime = new AnimeFX(this);
    this.juice.setAnime(this.anime);
    this.registry.set("anime", this.anime);
    this.sound2 = new SoundSystem(this);
    this.registry.set("sound2", this.sound2);

    // Throne at path end — what the creeps attack.
    const endCell = this.grid.getEndCell();
    this.throne = new Throne(this, endCell.x, endCell.y, STARTING_LIVES);
    this.throne.updateHpVisuals(STARTING_LIVES);

    // State — all 4 base elements pre-researched so the player can immediately build any base tower.
    // Element picks earned from waves go toward unlocking fusion combinations & upgrade tiers.
    this.state = {
      lives: STARTING_LIVES,
      gold: STARTING_GOLD,
      elements: { fire: 1, water: 1, earth: 1, nature: 1 },
      elementPoints: 0,
      totalGoldEarned: STARTING_GOLD,
      startedAt: this.time.now,
    };
    this.towers = [];
    this.projectiles = [];
    this.selectedTowerId = null;
    this.selectedCell = null;

    const diffKey = (this.registry.get("difficulty") as Difficulty | undefined) ?? "easy";
    const diffCfg = DIFFICULTY_CONFIGS[diffKey] ?? DIFFICULTY_CONFIGS.easy;
    this.waveMgr = new WaveManager(
      this,
      this.grid,
      (e) => {
        this.state.lives = Math.max(0, this.state.lives - e.def.damage);
        this.throne.onLeakHit(e.def.damage, this.state.lives);
        this.juice.onLeak();
        this.sound2.onLeak();
        this.perfStreak = 0;  // any leak breaks the perfect streak
        this.adapter.haptic("warning");
        this.emitState();
        if (this.state.lives <= 0) this.gameOver(false);
      },
      (e) => {
        this.state.gold += e.def.gold;
        this.state.totalGoldEarned += e.def.gold;
        this.juice.onKill(e.position.x, e.position.y, e.def.color);
        this.sound2.onKill();
        this.adapter.haptic("light");
        this.emitState();
      },
      diffCfg,
    );

    // Click handler for placement / selection
    this.input.on("pointerdown", (p: Phaser.Input.Pointer) => this.onPointerDown(p));

    // Launch the HUD overlay
    this.scene.launch("UIScene", {
      onPickElement: (el: Element) => this.pickElement(el),
      onChooseTower: (id: TowerId) => this.chooseTowerToPlace(id),
      onStartWave: () => this.tryStartWave(),
      onSell: () => this.sellSelectedTower(),
      onUpgrade: () => this.upgradeSelectedTower(),
      onRewardGold: (amount: number) => this.grantAdReward(amount),
      onBack: () => {
        this.scene.stop("UIScene");
        this.scene.start("MenuScene");
      },
    });

    this.events.on("shutdown", () => {
      this.scene.stop("UIScene");
      this.background?.destroy();
    });

    // Initial state push — no element-picker on start, players go straight to building.
    this.time.delayedCall(0, () => {
      this.emitState();
    });
  }

  update(_time: number, delta: number): void {
    const now = this.time.now;
    const deltaMs = delta;

    if (this.state.lives <= 0) return;

    // Anime hit-stop: freeze gameplay logic for punch while FX/tweens keep
    // animating. Skips creep/projectile/tower advancement this frame.
    if (this.anime.isFrozen(now)) return;

    // Cull destroyed towers (from creep attacks).
    for (let i = this.towers.length - 1; i >= 0; i--) {
      const t = this.towers[i];
      if (t.destroyed) {
        // Refund nothing — destroyed towers are gone.
        if (this.selectedCell === t.cell) this.clearSelectedCell();
        this.towers.splice(i, 1);
        // Schedule actual sprite cleanup after death animation.
        this.time.delayedCall(400, () => t.destroy());
        this.adapter.haptic("warning");
        this.emitState();
      }
    }

    if (this.waveMgr.spawning) {
      const { finished } = this.waveMgr.update(deltaMs, now, this.towers);
      if (finished) {
        const justCleared = this.waveMgr.current;
        const bonus = this.bonusForWave(justCleared);
        this.state.gold += bonus;
        this.state.totalGoldEarned += bonus;
        this.juice.onWaveClear();
        this.sound2.onWaveClear();
        this.adapter.haptic("success");
        // Perfect wave: no lives lost since wave start.
        if (this.state.lives >= this.livesAtWaveStart) {
          this.perfStreak++;
          // Streak ≥3 bonus: +10% of wave gold per wave (compounding reward)
          if (this.perfStreak >= 3) {
            const streakBonus = Math.floor(bonus * 0.1);
            this.state.gold += streakBonus;
            this.state.totalGoldEarned += streakBonus;
            this.streakBonusForPrompt = streakBonus;
          } else {
            this.streakBonusForPrompt = 0;
          }
        } else {
          this.streakBonusForPrompt = 0;
        }
        this.emitState();
        if (this.waveMgr.isFinalWaveCleared()) {
          this.gameOver(true);
          return;
        }
        this.grantElementPick();
      }
    }

    for (const t of this.towers) t.update(deltaMs, now, this.waveMgr.enemies, this.projectiles, this, this.juice);
    for (const p of this.projectiles) p.update(deltaMs, now);
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      if (!this.projectiles[i].alive) this.projectiles.splice(i, 1);
    }

    this.trackBoss();
  }

  /** Enemy ids that trigger the cinematic boss intro + persistent HP bar. */
  private static readonly BOSS_TIER = new Set(["boss", "overlord", "warlord", "worldender"]);

  /**
   * Watches for boss-tier creeps: plays a one-time anime intro on arrival,
   * streams live HP to the UIScene boss bar, and detonates an epic death
   * explosion when the boss falls.
   */
  private trackBoss(): void {
    // Find the toughest boss-tier creep currently alive.
    let boss: import("../entities/Enemy").Enemy | null = null;
    for (const e of this.waveMgr.enemies) {
      if (!e.alive) continue;
      if (!GameScene.BOSS_TIER.has(e.def.id)) continue;
      if (!boss || e.maxHp > boss.maxHp) boss = e;
    }

    if (boss && boss !== this.activeBoss) {
      this.activeBoss = boss;
      if (!this.bossIntroShownFor.has(boss)) {
        this.bossIntroShownFor.add(boss);
        this.playBossIntro(boss);
      }
      this.events.emit("boss-spawn", { label: boss.def.label, color: boss.def.color, maxHp: boss.maxHp });
    }

    if (this.activeBoss) {
      if (this.activeBoss.alive) {
        this.events.emit("boss-hp", { hp: Math.max(0, this.activeBoss.hp), maxHp: this.activeBoss.maxHp });
      } else {
        // Boss fell — epic finisher.
        const p = this.activeBoss.position;
        this.bossDeathExplosion(p.x, p.y, this.activeBoss.def.color);
        this.events.emit("boss-dead");
        this.activeBoss = null;
      }
    }
  }

  /** Cinematic boss arrival: dark pulse, converging speed lines, screen-wide shake. */
  private playBossIntro(boss: import("../entities/Enemy").Enemy): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height * 0.42;
    this.anime.flash(boss.def.color, 0.4, 220);
    this.anime.speedLines(cx, cy, boss.def.color, 22, 320);
    this.anime.impactStar(cx, cy, boss.def.color, 90);
    this.anime.hitStop(140);
    this.anime.zoomPunch(1.06, 120);
    this.cameras.main.shake(380, 0.012);
    this.adapter.haptic("heavy");
    this.sound2.onWaveStart();
  }

  /** Boss death: chained explosions, full impact frame, big shockwave. */
  private bossDeathExplosion(x: number, y: number, color: number): void {
    this.anime.impact(x, y, color, 1);
    this.anime.flash(0xffffff, 0.5, 180);
    this.cameras.main.shake(420, 0.016);
    this.adapter.haptic("heavy");
    // Chained secondary bursts around the corpse.
    for (let i = 0; i < 6; i++) {
      this.time.delayedCall(i * 90, () => {
        const ox = x + (Math.random() - 0.5) * 90;
        const oy = y + (Math.random() - 0.5) * 90;
        this.anime.impactStar(ox, oy, color, 30 + Math.random() * 30);
        this.juice.onKill(ox, oy, color);
      });
    }
  }

  // ---------- UI callbacks ----------

  private pickElement(el: Element): void {
    if (this.state.elementPoints <= 0) return;
    this.state.elementPoints--;
    this.state.elements[el]++;
    this.adapter.haptic("medium");
    this.emitState();
  }

  private chooseTowerToPlace(id: TowerId): void {
    const def = TOWERS[id];
    if (!def) return;
    if (!this.canBuild(def)) return;
    if (this.state.gold < def.cost) return;
    this.selectedTowerId = id;
    this.clearSelectedCell();
    if (!this.placementMarker) {
      this.placementMarker = this.add.rectangle(0, 0, 44, 44, def.color, 0.15);
      this.placementMarker.setStrokeStyle(2, def.color, 0.9);
      this.placementMarker.setVisible(false);
    } else {
      this.placementMarker.setFillStyle(def.color, 0.15);
      this.placementMarker.setStrokeStyle(2, def.color, 0.9);
    }
  }

  private tryStartWave(): void {
    if (!this.waveMgr.intermission) return;
    if (this.waveMgr.current >= this.waveMgr.totalWaves) return;
    this.livesAtWaveStart = this.state.lives;
    this.waveMgr.startWave(this.time.now);
    this.juice.onWaveStart();
    this.sound2.onWaveStart();
    this.towerBattleCry();
    // Anime hype: white flash + speed lines bursting from the spawn portal.
    const spawn = this.grid.getSpawnCell();
    this.anime.flash(0xa6e3ff, 0.22, 160);
    this.anime.speedLines(spawn.x, spawn.y, 0xa6e3ff, 14, 200);
    this.anime.zoomPunch(1.03, 90);
    this.adapter.haptic("medium");
    this.emitState();
  }

  /** Battle Cry — each tower emits a brief aura ring on wave-start, making the map "wake up". */
  private towerBattleCry(): void {
    for (const tower of this.towers) {
      const cx = tower.cell.x;
      const cy = tower.cell.y;
      const color = tower.def.color;

      // Outer expanding ring
      const ring = this.add.graphics();
      ring.lineStyle(2, color, 0.85);
      ring.strokeCircle(0, 0, 18);
      ring.x = cx;
      ring.y = cy;

      this.tweens.add({
        targets: ring,
        scaleX: 2.6,
        scaleY: 2.6,
        alpha: 0,
        duration: 550,
        ease: "Sine.easeOut",
        onComplete: () => ring.destroy(),
      });

      // Inner fill pulse (smaller, faster, more opaque)
      const pulse = this.add.graphics();
      pulse.fillStyle(color, 0.22);
      pulse.fillCircle(0, 0, 16);
      pulse.x = cx;
      pulse.y = cy;

      this.tweens.add({
        targets: pulse,
        scaleX: 1.8,
        scaleY: 1.8,
        alpha: 0,
        duration: 320,
        ease: "Sine.easeOut",
        onComplete: () => pulse.destroy(),
      });
    }
  }

  private sellSelectedTower(): void {
    if (!this.selectedCell) return;
    const idx = this.towers.findIndex((t) => t.cell === this.selectedCell);
    if (idx < 0) return;
    const t = this.towers[idx];
    // Refund 70% of total invested (base cost + each level upgrade cost).
    let invested = t.def.cost;
    for (let lvl = 2; lvl <= t.level; lvl++) {
      const mult = [1.0, 0.7, 1.0][lvl - 1];
      invested += Math.round(t.def.cost * mult);
    }
    const refund = Math.round(invested * 0.7);
    this.state.gold += refund;
    t.destroy();
    this.towers.splice(idx, 1);
    this.clearSelectedCell();
    this.adapter.haptic("medium");
    this.sound2.onSell();
    this.emitState();
  }

  private upgradeSelectedTower(): void {
    if (!this.selectedCell) return;
    const t = this.towers.find((x) => x.cell === this.selectedCell);
    if (!t) return;
    const cost = t.upgradeCost();
    if (cost == null || this.state.gold < cost) return;
    this.state.gold -= cost;
    t.upgrade(this);
    // Re-show range ring at new size
    t.showRange(this, false);
    t.showRange(this, true);
    this.adapter.haptic("success");
    this.sound2.onUpgrade();
    this.events.emit("tower-selected", t.def, { level: t.level, upgradeCost: t.upgradeCost() });
    this.emitState();
  }

  // ---------- placement ----------

  private onPointerDown(p: Phaser.Input.Pointer): void {
    if (p.y > this.grid.originY + this.grid.height) return; // tap on HUD
    const cell = this.grid.worldToCell(p.worldX, p.worldY);
    if (!cell) return;

    const existing = this.towers.find((t) => t.cell === cell);

    if (this.selectedTowerId) {
      // Placement mode: only allowed on buildable empty cells.
      if (cell.type !== "build" || existing) {
        this.cancelPlacement();
        return;
      }
      const def = TOWERS[this.selectedTowerId];
      if (!def || this.state.gold < def.cost) {
        this.cancelPlacement();
        return;
      }
      this.state.gold -= def.cost;
      const tower = new Tower(this, cell, def);
      this.towers.push(tower);
      this.adapter.haptic("medium");
      this.sound2.onPlace();
      this.cancelPlacement();
      this.emitState();
      return;
    }

    // Not placing: tap selects an existing tower (toggle range ring).
    this.clearSelectedCell();
    if (existing) {
      this.selectedCell = cell;
      existing.showRange(this, true);
      this.events.emit("tower-selected", existing.def, {
        level: existing.level,
        upgradeCost: existing.upgradeCost(),
      });
    } else {
      this.events.emit("tower-selected", null);
    }
  }

  private cancelPlacement(): void {
    this.selectedTowerId = null;
    this.placementMarker?.setVisible(false);
  }

  private clearSelectedCell(): void {
    if (!this.selectedCell) return;
    const t = this.towers.find((t) => t.cell === this.selectedCell);
    t?.showRange(this, false);
    this.selectedCell = null;
    this.events.emit("tower-selected", null);
  }

  // ---------- helpers ----------

  private canBuild(def: TowerDef): boolean {
    return def.recipe.every((el) => this.state.elements[el] > 0);
  }

  private grantElementPick(): void {
    // After every cleared wave, instead of asking the player to pick an element,
    // show a centered "WAVE X CLEARED" prompt with a NEXT WAVE button.
    const next = this.waveMgr.current + 1;
    this.events.emit("show-next-wave-prompt", {
      cleared: this.waveMgr.current,
      streakGold: this.streakBonusForPrompt,
      next,
      total: this.waveMgr.totalWaves,
      perfStreak: this.perfStreak,
    });
    this.emitState();
  }

  private bonusForWave(idx: number): number {
    return 30 + idx * 10;
  }

  private emitState(): void {
    this.events.emit("state", {
      ...this.state,
      wave: this.waveMgr.current,
      totalWaves: this.waveMgr.totalWaves,
      intermission: this.waveMgr.intermission,
      towersByCell: this.towers.map((t) => ({ cell: t.cell, def: t.def })),
      affordableTowers: TOWER_LIST.filter((t) => this.canBuild(t)).map((t) => t.id),
      selectedTowerCell: this.selectedCell,
    });
  }

  /** Grant gold after the player watches a rewarded ad. */
  private grantAdReward(amount: number): void {
    this.state.gold += amount;
    this.state.totalGoldEarned += amount;
    this.adapter.haptic("success");
    this.emitState();
  }

  private gameOver(victory: boolean): void {
    if (victory) this.sound2.onVictory();
    else this.sound2.onDefeat();
    this.scene.stop("UIScene");
    this.scene.start("GameOverScene", {
      victory,
      wavesCleared: victory ? this.waveMgr.totalWaves : Math.max(0, this.waveMgr.current - 1),
      totalWaves: this.waveMgr.totalWaves,
      goldEarned: this.state.totalGoldEarned,
      livesLeft: this.state.lives,
      durationMs: this.time.now - this.state.startedAt,
    });
  }

  // Used by element-picker UI from UIScene to know list.
  static elementOptions(): readonly Element[] {
    return ELEMENT_LIST;
  }

  static elementMeta() {
    return ELEMENT_META;
  }
}
